import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'gatsby';
import { Container, List, Row, Column } from '@pella/sash-framework';
import { Layout, SEO, Breadcrumbs, Image, CTA } from '../../components';
import { useRtaData } from '../../hooks/use-rta-data';
import { isValidArray, objectHasValues } from '../../components/utilities/validations';
import CardRenderer from '../../components/common/card-types/card-renderer';
import DynamicComponent from '../../components/common/dynamic-component';
import LimitedWarrantyV2 from '../../components/common/footer/limited-warranty-v2';
import CompetitiveBoxes from '../../components/template/category/competitive-boxes';
import ScheduleConsultation from '../../components/page/rta/v2/home';
import { HoverEmbed } from '../../components/third-party/hover';
import CardSection from '../../components/common/card-section';
import styles from './index.module.scss';

const Category = ({ data }) => {
	const rtaData = useRtaData();
	const pageData = data.contentstackCategory;
	const [activeTab, setActiveTab] = useState(0);

	const onTabClick = (i) => setActiveTab(i);

	return (
		<Layout
			id='template-Category'
			pageTitle={pageData.seo?.title}
		>
			{pageData.breadcrumbs && (
				<Breadcrumbs
					data={pageData.breadcrumbs}
					currentUrl={pageData.url}
				/>
			)}

			{pageData.top_of_fold && (
				<div className='category-top relative bg-neutral-50 pb-6 xl:pb-8 xl:pt-6'>
					{isValidArray(pageData.top_of_fold.image) && (
						<Image
							data={pageData.top_of_fold.image[0]}
							className={`${styles.cat_HeroImage} w-full md:!absolute`}
							loading='eager'
						/>
					)}

					{pageData.top_of_fold.heading && <h1 className={`${styles.cat_HeroHeading} absolute m-0 inline-block bg-black py-2 pl-6 pr-8 text-white md:relative md:pl-8 xl:pl-16`}>{pageData.top_of_fold.heading}</h1>}

					<Container className={`${styles.cat_ProductGrid} relative grid`}>
						{isValidArray(pageData.top_of_fold.cob) && (
							<Image
								data={pageData.top_of_fold.cob[0]}
								className={`${styles.cat_ProductCob} my-6 md:my-0`}
								objectFit='contain'
								loading='eager'
							/>
						)}

						{pageData.top_of_fold.product_info && (
							<div className={`${styles.cat_ProductInfo} self-end bg-white`}>
								{pageData.top_of_fold.product_info.heading && <h2 className='m-0 bg-black px-1 py-3 text-center text-white md:px-3'>{pageData.top_of_fold.product_info.heading}</h2>}
								<div
									className='px-5 py-3 md:px-6 md:py-4 [&_h3+p]:mt-0 [&_h3]:mt-6'
									dangerouslySetInnerHTML={{ __html: pageData.top_of_fold.product_info.points }}
								/>
							</div>
						)}

						<div className={`${styles.cat_ProductFeatures} mt-6 md:mt-0 xl:mt-5`}>
							{pageData.top_of_fold.subheading && <p className='text-subtitle font-bold'>{pageData.top_of_fold.subheading}</p>}

							{isValidArray(pageData.top_of_fold.feature) && objectHasValues(pageData.top_of_fold.feature[0]) && (
								<List
									bullets='none'
									className={`-mx-1.5 mb-4 mt-3 flex flex-wrap`}
								>
									{pageData.top_of_fold.feature.map((feat, index) => (
										<li
											key={index}
											className='basis-6/12 p-1.5 md:flex-1'
										>
											<div className={`cat-featureCard h-full bg-white px-4 py-8 md:p-6`}>
												<CardRenderer
													data={{
														card_type: 'icon',
														heading: feat.heading,
														image: [{ image: feat.file }],
													}}
												/>
											</div>
										</li>
									))}
								</List>
							)}

							{isValidArray(pageData.top_of_fold.cta) && (
								<List bullets='none'>
									{pageData.top_of_fold.cta.map((cta, index) => (
										<li key={index}>
											<CTA
												data={cta}
												size='sm'
											/>
										</li>
									))}
								</List>
							)}
						</div>
					</Container>
				</div>
			)}

			{isValidArray(pageData.modular_blocks) &&
				pageData.modular_blocks.map((block, index) => {
					if (block.bulleted_list != null) {
						return (
							<div
								key={index}
								className='py-6 md:py-8 xl:py-10'
							>
								<Container>
									{block.bulleted_list.heading && <h2>{block.bulleted_list.heading}</h2>}
									{block.bulleted_list.subheading && <p className='text-subtitle mt-1'>{block.bulleted_list.subheading}</p>}

									<CompetitiveBoxes data={block.bulleted_list} />
								</Container>
							</div>
						);
					}

					if (block.card != null) {
						return (
							<CardSection
								data={block.card}
								key={index}
							/>
						);
					}

					if (isValidArray(block.all_dynamic?.dynamic)) {
						if (block.all_dynamic.dynamic[0].card_type !== 'single-article-slider') {
							return (
								<DynamicComponent
									key={index}
									data={block.all_dynamic}
									className={`my-0 py-6 md:py-8 xl:py-10 cat-${block.all_dynamic.dynamic?.[0]?.card_type?.replace(' ', '-')}`}
								/>
							);
						} else {
							return (
								<div
									key={index}
									className='bg-neutral-50 py-6 md:py-8 xl:py-10'
								>
									<Container>
										<Row align='center'>
											<Column
												xs={12}
												md='fluid'
											>
												{block.all_dynamic.heading && <h2>{block.all_dynamic.heading}</h2>}
												{block.all_dynamic.subheading && <p className='text-subtitle mb-4 xl:mb-6'>{block.all_dynamic.subheading}</p>}
											</Column>

											<Column
												xs={12}
												md={9}
												xl={6}
											>
												<DynamicComponent
													data={block.all_dynamic}
													className={`my-0 cat-${block.all_dynamic.dynamic?.[0]?.card_type?.replace(' ', '-')}`}
												/>

												{block.all_dynamic.dynamic?.[0]?.ctas && (
													<div className='mt-4 text-center xl:mt-6'>
														<CTA data={block.all_dynamic.dynamic[0].ctas[0]} />
													</div>
												)}
											</Column>
										</Row>
									</Container>
								</div>
							);
						}
					}

					if (block.tiles != null) {
						return (
							<div
								key={index}
								className='bg-neutral-50 py-6 md:py-8 xl:py-10'
							>
								{block.tiles.heading && (
									<Container>
										<h2>{block.tiles.heading}</h2>
									</Container>
								)}

								{block.tiles.tab.length > 1 && (
									<div className='mb-4 overflow-auto xl:mb-6'>
										<Container>
											<List
												bullets='none'
												className={`${styles.cat_TilesTabs} inline-flex`}
											>
												{block.tiles.tab.map((tab, index) => (
													<li
														key={index}
														className='relative flex-initial'
													>
														<button
															onClick={() => onTabClick(index)}
															className={`pb-1 pt-2 font-semibold ${index == activeTab ? `${styles.cat_TilesTab_active} text-black` : 'text-neutral'}`}
														>
															{tab.heading}
														</button>
													</li>
												))}
											</List>
										</Container>
									</div>
								)}

								{block.tiles.tab.map((tab, index) => (
									<div
										key={index}
										className={`${styles.cat_TilesPanel} ${index == activeTab ? `${styles.cat_TilesPanel_active} block` : 'hidden'}`}
									>
										{isValidArray(tab.dynamic) && (
											<DynamicComponent
												data={tab}
												className={styles.cat_TilesSlider}
												key={`active-${activeTab}`}
											/>
										)}
									</div>
								))}
							</div>
						);
					}

					if (block.forms != null) {
						return (
							<div
								key={index}
								className='py-6 md:py-8 xl:py-10'
							>
								<Container>
									{block.forms.heading && <h2 className='mb-2'>{block.forms.heading}</h2>}
									{block.forms.subheading && <p className='text-subtitle mb-6'>{block.forms.subheading}</p>}
									{block.forms.description && (
										<div
											className={styles.catFormDesc}
											dangerouslySetInnerHTML={{ __html: block.forms.description }}
										/>
									)}

									<div className={`${styles.catForm} mt-8 p-4 md:mt-10 md:p-6 xl:p-10`}>
										{block.forms.form_heading && <p className='mb-6 md:text-xl xl:mb-10'>{block.forms.form_heading}</p>}

										{block.forms.form_type === 'RTA' && typeof window !== 'undefined' && (
											<ScheduleConsultation
												data={rtaData}
												location={window.location}
												layout='modal'
												ctaText='See Availability'
												referrer='whereToBuy'
											/>
										)}
									</div>
								</Container>
							</div>
						);
					}

					if (block.embed != null) {
						return (
							<div
								key={index}
								id={block.embed.anchor_text}
								className='mt-6'
							>
								<HoverEmbed configId={block.embed.embed_id} />
							</div>
						);
					}

					if (block.image_aside != null) {
						return (
							<LimitedWarrantyV2
								data={block.image_aside}
								key={index}
							/>
						);
					}
				})}
		</Layout>
	);
};

Category.propTypes = {
	data: PropTypes.object,
};

// eslint-disable-next-line react/prop-types -- fix this
export const Head = ({ data }) => (
	<SEO
		// eslint-disable-next-line react/prop-types -- fix this
		{...data.contentstackCategory.seo}
	/>
);

export default Category;

export const query = graphql`
	query ($uid: String!) {
		contentstackCategory(id: { eq: $uid }) {
			url
			breadcrumbs {
				active_page
				back_pages {
					...CTA
				}
			}
			top_of_fold {
				image {
					...Image
					image {
						url
						gatsbyImageData(quality: 90, layout: FULL_WIDTH)
					}
				}
				heading
				cob {
					...Image
					image {
						url
						gatsbyImageData(quality: 90, height: 600)
					}
				}
				product_info {
					heading
					points
				}
				subheading
				feature {
					file {
						url
						gatsbyImageData(quality: 90)
					}
					heading
				}
				cta {
					...CTA
				}
			}
			modular_blocks {
				bulleted_list {
					heading
					subheading
					feature {
						heading
						description
					}
					black
					file {
						url
						gatsbyImageData(quality: 90)
					}
					body
					cta {
						...CTA
					}
				}
				card {
					...CardSection
				}
				tiles {
					heading
					tab {
						heading
						subheading
						dynamic {
							...Dynamic
						}
					}
				}
				all_dynamic {
					heading
					subheading
					dynamic {
						...Dynamic
					}
				}
				forms {
					heading
					subheading
					description
					form_heading
					form_type
				}
				embed {
					anchor_text
					embed_id
				}
				image_aside {
					...ImageAside
				}
			}
			seo {
				title
				description
				noIndex: no_index_page
			}
		}
	}
`;
